import { _ } from "~/i18n";
import { icon, node } from "~/util/dom";

interface IContent {
	button?: HTMLButtonElement;
	content: HTMLDivElement;
}

export interface IMyMapsDialog {
	titles?: {
		main?: Array<string>;
		saveBtn?: string;
		cancelBtn?: string;
		throwAwayTitle?: string;
		throwAwayConfirm?: string;
		throwAwayCancel?: string;
	};
	defaultContent?: number;
	onSave?: () => void;
	onCancel?: () => void;
	onThrowAwayConfirm?: () => void;
	onThrowAwayCancel?: () => void;
	onClose?: () => void;
	onThrowAwayTest?: () => boolean;
}

export default class MyMapsDialog {
	_opts: IMyMapsDialog;

	_dom: {
		[key: string]: HTMLElement;
	};

	_contents: Array<IContent>;

	_isOpen: boolean;

	_isThrowOpen: boolean;

	_activeContent: IMyMapsDialog["defaultContent"];

	_isLoading: boolean;

	constructor(opts: IMyMapsDialog) {
		this._opts = {
			defaultContent: 0,
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onThrowAwayConfirm: () => {},
			onThrowAwayCancel: () => {},
			onThrowAwayTest: () => true,
			...opts,
			titles: {
				main: ["*Main title*"],
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
				throwAwayTitle: _("note.throwAwaySaving2"),
				throwAwayConfirm: _("detail.content.review2.throwAway"),
				throwAwayCancel: _("cancel"),
				...opts.titles,
			},
		};
		this._dom = {};
		this._isOpen = false;
		this._isThrowOpen = false;
		this._activeContent = this._opts.defaultContent;
		this._contents = [];
		this._isLoading = false;

		this._build();
		this.setContent(this._opts.defaultContent);
	}

	get contents() {
		return this._contents.map(item => item.content);
	}

	get dom() {
		return this._dom;
	}

	get activeContent() {
		return this._activeContent;
	}

	set error(error: string) {
		this._dom.error.textContent = error;
	}

	updateOpts(opts: Partial<IMyMapsDialog>) {
		Object.assign(this._opts, opts);
	}

	set setSaveBtnTitle(value: string) {
		this._dom.saveBtnText.textContent = value;
	}

	set loading(loading: boolean) {
		this._isLoading = loading;
		this._dom.buttons.classList[loading ? "add" : "remove"]("loading");

		if (this._isLoading) {
			this._dom.buttons.append(this._dom.saveBtnLoader);
		} else {
			this._dom.saveBtnLoader.remove();
		}
	}

	setContent(activeInd: number) {
		this._activeContent = activeInd;

		for (let ind = 0, max = this._contents.length; ind < max; ind++) {
			const { content, button } = this._contents[ind];

			button?.classList.remove("btn-active");

			if (ind === activeInd) {
				button?.classList.add("btn-active");
				this._dom.tabsContent.append(content);
			} else {
				content.remove();
			}
		}
	}

	handleEvent(event: KeyboardEvent) {
		if (event.type === "keydown") {
			if (event.code === "Escape") {
				this.close();
			}
		}
	}

	open() {
		if (!this._isOpen) {
			document.body.append(this._dom.cover);
			document.body.classList.add("disable-scroll");
			this._isOpen = true;
		}
	}

	close(force?: boolean) {
		if (!this._isOpen) {
			return;
		}

		const throwAwayTest = this._opts.onThrowAwayTest();

		if (throwAwayTest || force) {
			document.removeEventListener("keydown", this);
			this._dom.cover.remove();
			document.body.classList.remove("disable-scroll");
			this._isOpen = false;
		} else {
			this._addThrowAway();
		}
	}

	destroy() {
		this.close(true);
	}

	_build() {
		const container = node("div", { className: "mymaps-dialog" });
		const content = node("div", { className: "mymaps-dialog__content" });
		const tabsContent = node("div", { className: "mymaps-dialog__tabsContent" });
		const buttons = node("div", { className: "mymaps-dialog__buttons" });
		const closeBtn = node("button", { className: "mymaps-dialog__closeBtn", type: "button" });
		const saveBtn = node("button", { className: "mymaps-dialog__saveBtn", type: "button" });
		const cancelBtn = node("button", { className: "mymaps-dialog__cancelBtn", type: "button", textContent: this._opts.titles.cancelBtn });
		const cover = node("div", { className: "mymaps-dialog__cover" });
		const header = node("div", { className: "mymaps-dialog__header" });
		const error = node("p", { className: "mymaps-dialog__error" });
		const saveBtnLoader = node("img", { className: "mymaps-dialog__saveBtnLoader", src: "/img/loader.gif" });
		const saveBtnText = node("span", { className: "mymaps-dialog__saveBtnText", textContent: this._opts.titles.saveBtn });

		// mame pouze jeden
		if (this._opts.titles.main.length === 1) {
			const title = node("h2", { className: "mymaps-dialog__title", textContent: this._opts.titles.main[0] });
			const tabContent = node("div", { className: "mymaps-dialog__content" });

			this._contents.push({
				button: null,
				content: tabContent,
			});
			header.append(title);
		} else {
			// taby
			const tabsSwitcher = node("div", { className: "mymaps-dialog__tabsSwitcher" });

			for (let ind = 0, max = this._opts.titles.main.length; ind < max; ind++) {
				const tabTitle = this._opts.titles.main[ind];
				const tabsSwitcherItem = node("button", { className: "mymaps-dialog__tabsSwitcherItem", type: "button", textContent: tabTitle });
				const tabContent = node("div", { className: "mymaps-dialog__content" });

				tabsSwitcherItem.addEventListener("click", () => {
					this.setContent(ind);
				});
				tabsSwitcher.append(tabsSwitcherItem);

				this._contents.push({
					button: tabsSwitcherItem,
					content: tabContent,
				});
			}

			header.classList.add("has-tabs");
			header.append(tabsSwitcher);
		}

		saveBtn.setAttribute("data-log-ignore", "1");
		saveBtn.append(saveBtnText);
		closeBtn.append(icon("close-thin"));
		buttons.append(saveBtn, cancelBtn);
		content.append(header, tabsContent, error, buttons);
		container.append(closeBtn, content);
		cover.append(container);
		document.addEventListener("keydown", this);
		closeBtn.addEventListener("click", () => {
			this.close();
		});
		saveBtn.addEventListener("click", () => {
			if (this._isLoading) {
				return;
			}

			this._opts.onSave();
		});
		cancelBtn.addEventListener("click", () => {
			this._opts.onCancel();
		});

		let isCoverClick = false;

		cover.addEventListener("pointerdown", event => {
			isCoverClick = !container.contains(event.target as HTMLElement);
		});
		cover.addEventListener("pointerup", () => {
			if (isCoverClick) {
				this.close();
			}

			isCoverClick = false;
		});

		this._dom.container = container;
		this._dom.cover = cover;
		this._dom.tabsContent = tabsContent;
		this._dom.error = error;
		this._dom.saveBtn = saveBtn;
		this._dom.saveBtnLoader = saveBtnLoader;
		this._dom.buttons = buttons;
		this._dom.saveBtnText = saveBtnText;
	}

	_addThrowAway() {
		if (this._isThrowOpen) {
			return;
		}

		const throwAwayCover = node("div", { className: "mymaps-dialog__throwAwayCover" });
		const throwAway = node("div", { className: "mymaps-dialog__throwAway" });
		const throwAwayInner = node("div", { className: "mymaps-dialog__throwAwayInner" });
		const throwAwayTitle = node("span", { className: "mymaps-dialog__throwAwayTitle", textContent: this._opts.titles.throwAwayTitle });
		const throwAwayConfirmBtn = node("button", { className: "mymaps-dialog__throwAwayConfirmBtn", type: "button", textContent: this._opts.titles.throwAwayConfirm });
		const throwAwayCancelBtn = node("button", { className: "mymaps-dialog__throwAwayCancelBtn", type: "button", textContent: this._opts.titles.throwAwayCancel });

		throwAwayConfirmBtn.addEventListener("click", () => {
			this._removeThrowAway();
			this._opts.onThrowAwayConfirm();
		});
		throwAwayCancelBtn.addEventListener("click", () => {
			this._removeThrowAway();
			this._opts.onThrowAwayCancel();
		});
		throwAwayInner.append(throwAwayTitle, throwAwayConfirmBtn, throwAwayCancelBtn);
		throwAway.append(throwAwayInner);
		throwAwayCover.append(throwAway);

		this._dom.throwAwayCover = throwAwayCover;
		this._dom.container.append(throwAwayCover);
		this._isThrowOpen = true;
	}

	_removeThrowAway() {
		if (this._dom.throwAwayCover) {
			this._dom.throwAwayCover.remove();
			this._dom.throwAwayCover = null;
			this._isThrowOpen = false;
		}
	}
}

